import { useState, useEffect } from 'react'
import { JobsService } from '@/services/jobs'
import { Job, NewJob, JobStatus } from '@/types/admin'
import { JobsTable } from '@/components/admin/jobs/JobsTable'
import { JobDialog } from '@/components/admin/jobs/JobDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { PlusCircle, Search, Filter } from 'lucide-react'
import { toast } from 'sonner'

export default function JobsPage() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingJob, setEditingJob] = useState<Job | null>(null)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const fetchJobs = async () => {
    try {
      const data = await JobsService.getAll()
      setJobs(data)
    } catch (error) {
      toast.error('Erro ao carregar vagas')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchJobs()
  }, [])

  const handleCreate = () => {
    setEditingJob(null)
    setDialogOpen(true)
  }

  const handleEdit = (job: Job) => {
    setEditingJob(job)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: NewJob) => {
    try {
      if (editingJob) {
        await JobsService.update(editingJob.id, data)
        toast.success('Vaga atualizada com sucesso')
      } else {
        await JobsService.create(data)
        toast.success('Vaga criada com sucesso')
      }
      fetchJobs()
    } catch (error) {
      toast.error('Erro ao salvar vaga')
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Tem certeza que deseja excluir esta vaga?')) return
    try {
      await JobsService.delete(id)
      toast.success('Vaga excluída')
      fetchJobs()
    } catch (error) {
      toast.error('Erro ao excluir vaga')
    }
  }

  // Filter Logic
  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.school_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.city.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === 'all' || job.status === statusFilter

    return matchesSearch && matchesStatus
  })

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800">Gerenciar Vagas</h1>
          <p className="text-slate-500 mt-1">
            Visualize e gerencie as oportunidades abertas.
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-brand-emerald hover:bg-emerald-600"
        >
          <PlusCircle className="mr-2 h-4 w-4" /> Nova Vaga
        </Button>
      </div>

      <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm space-y-4 md:space-y-0 md:flex md:items-center md:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por escola, cargo ou cidade..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-full md:w-56">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-400" />
                <SelectValue placeholder="Filtrar por Status" />
              </div>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os Status</SelectItem>
              <SelectItem value="new">Nova</SelectItem>
              <SelectItem value="analyzing">Em Análise</SelectItem>
              <SelectItem value="candidates_sent">
                Candidatos Enviados
              </SelectItem>
              <SelectItem value="closed">Fechada</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-emerald mx-auto mb-4"></div>
          <p className="text-slate-500">Carregando vagas...</p>
        </div>
      ) : (
        <JobsTable
          jobs={filteredJobs}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      <JobDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        job={editingJob}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
